<script>
	import Modal from '@/pages/components/modal.vue';
	import Folder from '@/pages/layouts/partials/sidebar/folder.vue';
	import MenuItem from '@/pages/layouts/partials/sidebar/menu-item.vue';
	import MenuService from '@/helpers/MenuService';
	import { useForm } from '@inertiajs/vue3';
	import SortableManager from '@/base/menu/SortableManager';
	import EntryHierarchy from '@/base/menu/EntryHierarchy';
	import DOMOrderSync from '@/base/menu/DOMOrderSync';
	import EntryValidator from '@/base/menu/EntryValidator';
	import EntryManager from '@/base/menu/EntryManager';
	import Permissions from "@/mixins/shared/permissions";

	export default {
		components: {Modal, Folder, MenuItem},
		mixins: [Permissions],
		data() {
			const entryHierarchy = new EntryHierarchy();
			return {
				form: useForm({
					entries: this.getEntries(),
				}),
				sortableManager: new SortableManager(),
				entryHierarchy: entryHierarchy,
				domOrderSync: new DOMOrderSync(),
				entryValidator: new EntryValidator(),
				entryManager: new EntryManager(entryHierarchy)
			}
		},
		mounted() {
			this.initSortable();
			$(document).on('wwdev.entry.delete', (event, entry) => this.deleteEntry(entry));
			$(document).off('.menu-edit-modal')
				.on('wwdev.menu.updated.menu-edit-modal', () => {
					this.form.entries = this.getEntries();
					this.$nextTick(() => {
						this.initSortable();
					});
				});
		},
		beforeUnmount() {
			this.sortableManager.destroyAll();
			$(document).off('wwdev.entry.delete');
			$(document).off('.menu-edit-modal');
		},
		provide() {
			return {
				form: this.form
			}
		},
		methods: {
			open() {
				this.$refs.modal.open();
				this.$nextTick(() => {
					this.initSortable();
				});
			},
			close() {
				this.$refs.modal.close();
			},
			initSortable() {
				this.sortableManager.destroyAll();
				this.sortableManager.createMainSortable(
					this.$refs.sortable,
					() => this.updateOrder()
				);
				this.sortableManager.initializeFolderSortables(
					(to, from, dragEl) => this.canDropInFolder(dragEl),
					() => this.updateOrder()
				);
			},
			canDropInFolder(dragElement) {
				const flatEntries = this.entryHierarchy.flatten(this.form.entries);
				return this.entryValidator.canDropInFolder(dragElement, flatEntries);
			},
			updateOrder() {
				const flatEntries = this.entryHierarchy.flatten(this.form.entries);
				this.domOrderSync.updateFromDOM(this.$refs.sortable, null, 1, flatEntries);
				this.form.entries = this.entryHierarchy.build(flatEntries);

				this.$nextTick(() => {
					this.sortableManager.initializeFolderSortables(
						(to, from, dragEl) => this.canDropInFolder(dragEl),
						() => this.updateOrder()
					);
				});
			},
			getEntries() {
				return this.parseEntries(MenuService.getEntries());
			},
			parseEntries(entries, parentId = null) {
				let result = [];
				for (let index in entries) {
					let entry = entries[index];
					let row = {
						id: entry.id,
						label: entry.label,
						icon: entry.icon,
						is_folder: entry.is_folder,
						parent_id: parentId,
						children: this.parseEntries(entry.children, entry.id),
						prio: parseInt(index) + 1
					}
					if (entry.menuItem) {
						row.menu_item_id = entry.menuItem.id;
						row.menu_item_label = entry.menuItem.label;
						row.menu_item_icon = entry.menuItem.icon;
					}
					if(entry.user_id == null) {
						row.canonical_id = entry.id;
					} else {
						row.canonical_id = entry.canonical_id;
					}

					result.push(row);
				}
				return result;
			},
			async addFolder() {
				const newId = this.entryManager.addFolder(this.form.entries);

				await this.$nextTick();

				this.sortableManager.initializeFolderSortables(
					(to, from, dragEl) => this.canDropInFolder(dragEl),
					() => this.updateOrder()
				);

				const ref = this.$refs[`entry-${newId}`]?.[0];
				if (ref?.open) {
					await ref.open();
				}
			},
			async addLink() {
				const newId = this.entryManager.addLink(this.form.entries);

				await this.$nextTick();

				const ref = this.$refs[`entry-${newId}`]?.[0];
				if (ref?.open) {
					await ref.open();
				}
			},
			deleteEntry(entryToDelete) {
				this.form.entries = this.entryManager.deleteEntry(entryToDelete, this.form.entries);

				this.$nextTick(() => {
					this.sortableManager.initializeFolderSortables(
						(to, from, dragEl) => this.canDropInFolder(dragEl),
						() => this.updateOrder()
					);
				});
			},
			async reset() {
				let result = await Helpers.Alert.confirm('Weet je zeker dat je het menu wilt resetten? Alle aanpassingen gaan verloren.');

				if (!result) {
					return;
				}

				Helpers.Ajax.delete(route('api.v1.menu.reset'), this.form.data(), {
					afterSuccess: () => {
						MenuService.updateMenu();
						this.$refs.modal.close();
					}
				});
			},
			submit() {
				let invalidEntries = [];

				$(this.$refs.sortable).find(':invalid').each((index, element) => {
					let $element = $(element);
					let $wrapper = $element.closest('[data-entry-id]');

					const entryId = $wrapper.data('entry-id');
					if (!invalidEntries.includes(entryId)) {
						invalidEntries.push(entryId);
					}

					this.form.errors[$element.attr('name')] = 'Dit veld is verplicht.';
				});

				for (let entryId of invalidEntries) {
					let ref = this.$refs[`entry-${entryId}`]?.[0];
					if (ref?.open) {
						ref.open();
					}
				}

				if (invalidEntries.length > 0) {
					return;
				}

				Helpers.Ajax.put(route('api.v1.menu.update'), this.form.data(), {
					afterSuccess: () => {
						MenuService.updateMenu();
						this.$refs.modal.close();
					}
				});
			}
		}
	}
</script>

<template>
	<Modal ref="modal" id="sidebar-modal" size="xl" title="Menu bewerken">
		<form @submit.prevent="submit" id="menu-edit-form" class="d-flex flex-column gap-3" ref="sortable" novalidate>
			<component
				v-for="entry in form.entries"
				:key="entry.id"
				:ref="`entry-${entry.id}`"
				:is="entry.is_folder ? 'Folder' : 'MenuItem'"
				:entry="entry"
				:data-entry-id="entry.id" />
		</form>

		<template #footer>
			<div class="d-flex flex-wrap gap-2 justify-content-between w-100">
				<div class="d-flex flex-wrap gap-2">
					<button v-if="can('edit menu content')" @click="addFolder()" type="button" class="btn btn-primary">
						<i class="bi bi-folder-plus"></i>
						Map toevoegen
					</button>
					<button v-if="can('edit menu content')"  @click="addLink()" type="button" class="btn btn-primary">
						<i class="bi bi-link-45deg"></i>
						Link toevoegen
					</button>
				</div>
				<div class="d-flex flex-wrap gap-2">
					<button @click="reset()" type="button" class="btn btn-danger">Menu resetten</button>
					<button @click="close()" type="button" class="btn btn-secondary">Annuleren</button>
					<button type="submit" form="menu-edit-form" class="btn btn-primary">Opslaan</button>
				</div>
			</div>
		</template>
	</Modal>
</template>

<style scoped>
	.sortable-ghost {
		opacity: 0.4;
		background: #f8f9fa;
	}

	.sortable-chosen {
		opacity: 0.8;
	}

	.sortable-drag {
		opacity: 0;
	}
</style>